export const STEP_TYPES = {
  STRUCTURED: 'structured',
  DATA_VIEW: 'data-view',
  TOOL_SELECTOR: 'tool-selector',
  CODE_EDITOR: 'code-editor',
  CODE_RUNNER: 'code-runner',
  LOGIC_FORM: 'logic-form',
  ANALOGY: 'analogy',
  STORY: 'story',
};

const TOOLS = {
  python: [
    'variables', 'lists', 'dicts', 'loops', 'functions', 'classes',
    'if/else', 'try/except', 'print()', 'len()', 'range()', 'enumerate()', 'zip()',
    'list comprehension', 'f-strings', 'modules', 'file I/O',
  ],
  'data-analysis': [
    'pandas', 'numpy', 'matplotlib', 'seaborn', 'plotly',
    'DataFrame', 'Series', 'groupby', 'merge', 'apply', 'map',
    'fillna', 'dropna', 'isnull', 'read_csv', 'read_excel',
    'describe', 'value_counts', 'sort_values', 'pivot_table',
    'loc', 'iloc', 'query', 'reset_index', 'rename', 'astype',
  ],
  sql: [
    'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT',
    'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN',
    'COUNT()', 'SUM()', 'AVG()', 'MAX()', 'MIN()',
    'DISTINCT', 'CASE WHEN', 'subquery', 'CTE (WITH)', 'UNION',
  ],
  excel: [
    'SUM', 'AVERAGE', 'COUNT', 'COUNTA', 'COUNTIF', 'SUMIF',
    'VLOOKUP', 'XLOOKUP', 'INDEX/MATCH', 'IF', 'IFS', 'AND', 'OR',
    'PivotTable', 'Filter', 'Sort', 'Power Query', 'Named Range',
    'Data Validation', 'Conditional Formatting', 'Charts',
  ],
  generic: ['research', 'outline', 'draft', 'review', 'structure', 'template', 'checklist'],
};

const SOLVE_CONFIGS = {
  python: {
    split: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What is the task asking you to produce or do?',
        'What inputs does it start with?',
        'What does the finished output look like?',
      ],
    },
    observe: {
      type: STEP_TYPES.DATA_VIEW,
      hints: ['What are the inputs?', 'What should a small test case look like?', 'What does success look like?'],
    },
    look: {
      type: STEP_TYPES.TOOL_SELECTOR,
      tools: TOOLS.python,
      prompt: 'Which Python tools or concepts will you use?',
    },
    verify: {
      type: STEP_TYPES.CODE_EDITOR,
      language: 'python',
      placeholder: '# Write an assertion to confirm your expected result\nassert result == expected_value',
      hint: 'Write a check before you build the full solution. This keeps you honest.',
    },
    evolve: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'python',
      placeholder: '# Write your complete solution here\n\n',
      hint: 'Run your code and see the output. Improve until it works.',
    },
  },

  'data-analysis': {
    split: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What question are we trying to answer from this data?',
        'What data do we have available?',
        'What does the final output or chart look like?',
      ],
    },
    observe: {
      type: STEP_TYPES.DATA_VIEW,
      hints: [
        'How many rows and columns?',
        'What are the column names and data types?',
        'Are there any missing values, duplicates, or odd values?',
      ],
    },
    look: {
      type: STEP_TYPES.TOOL_SELECTOR,
      tools: TOOLS['data-analysis'],
      prompt: 'Which pandas or plotting tools will you use?',
    },
    verify: {
      type: STEP_TYPES.CODE_EDITOR,
      language: 'python',
      placeholder: 'import pandas as pd\n\n# Validate your data before analysis\nassert df.shape[1] >= 3\nassert df.isnull().sum().sum() == 0',
      hint: 'Check shape, types, and nulls before you start analysing.',
    },
    evolve: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'python',
      placeholder: 'import pandas as pd\nimport matplotlib\nmatplotlib.use("Agg")\nimport matplotlib.pyplot as plt\n\n# Write your full analysis here\n',
      hint: 'Run the analysis. To show a chart, use the snippet in the hint panel.',
    },
  },

  sql: {
    split: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What data do we need to retrieve or calculate?',
        'Which tables are involved?',
        'What filters, groupings, or ordering are needed?',
      ],
    },
    observe: {
      type: STEP_TYPES.DATA_VIEW,
      hints: ['What columns exist?', 'What does a sample row look like?', 'What relationships exist between tables?'],
    },
    look: {
      type: STEP_TYPES.TOOL_SELECTOR,
      tools: TOOLS.sql,
      prompt: 'Which SQL clauses or functions will you use?',
    },
    verify: {
      type: STEP_TYPES.CODE_EDITOR,
      language: 'duckdb',
      placeholder: '-- Sanity-check the data before writing the full query\nSELECT * FROM lesson_data LIMIT 5;',
      hint: 'Peek at the table shape and sample rows. DuckDB loads lesson_data automatically.',
    },
    evolve: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'duckdb',
      placeholder: '-- Write your complete query\nSELECT\n  column1,\n  COUNT(*) AS total\nFROM lesson_data\nGROUP BY column1\nORDER BY total DESC;',
      hint: 'DuckDB runs analytical SQL in-browser. lesson_data is pre-loaded from the lesson context.',
    },
  },

  excel: {
    split: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What does this task ask you to build, calculate, or organise?',
        'What raw data or existing spreadsheet are you starting from?',
        'What does the finished workbook look like — what can someone do with it?',
      ],
    },
    observe: {
      type: STEP_TYPES.DATA_VIEW,
      hints: [
        'Open the file — what sheets, columns, and data are already there?',
        'What is missing, broken, or inconsistent that needs fixing first?',
        'What does a correct result look like — manually sketch the output',
      ],
    },
    look: {
      type: STEP_TYPES.TOOL_SELECTOR,
      tools: TOOLS.excel,
      prompt: 'Which Excel features or formulas will you use — and what does each one do for this task?',
    },
    verify: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'How will you check the formula or feature gives the right answer?',
        'Test with a known value — what do you expect, and what did you get?',
        'What edge cases could break it (blank cells, text in number columns, duplicates)?',
      ],
    },
    evolve: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'List every step you actually took in Excel (click by click, formula by formula)',
        'Paste or write the exact formulas, feature names, or Power Query steps you used',
        'What would you do differently to make this more robust or reusable?',
      ],
    },
  },

  generic: {
    split: {
      type: STEP_TYPES.STRUCTURED,
      prompts: ['What is the task asking you to do?', 'What do you start with?', 'What does done look like?'],
    },
    observe: {
      type: STEP_TYPES.DATA_VIEW,
      hints: ['What examples help you understand this?', 'What does a correct result look like?'],
    },
    look: {
      type: STEP_TYPES.TOOL_SELECTOR,
      tools: TOOLS.generic,
      prompt: 'What approaches or tools will you use?',
    },
    verify: {
      type: STEP_TYPES.STRUCTURED,
      prompts: ['How will you know your answer is correct?', 'What could go wrong?'],
    },
    evolve: {
      type: STEP_TYPES.STRUCTURED,
      prompts: ['Write your complete solution or answer', 'How could you improve it?'],
    },
  },
};

const FIVE_HOW_CONFIGS = {
  python: {
    practical: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'python',
      placeholder: 'import pandas as pd\nfrom io import StringIO\n\n# Repo proof belongs in GitHub, for example:\n# foundation/src/etl.py\n# Use this runner for a tiny scenario-matched proof only.\n\nsample_csv = """store_id,order_id,order_value,status\nUK01,1001,120.50,Complete\nUK01,1001,120.50,Complete\nUK02,1002,,Complete\nUK03,1003,80.00,\n"""\n\ndf = pd.read_csv(StringIO(sample_csv))\n\nprint("Rows in:", len(df))\nprint("Duplicate rows:", df.duplicated().sum())\nprint("Missing cells:", df.isnull().sum().sum())\n\nclean_df = df.drop_duplicates()\nclean_df = clean_df.dropna(subset=["order_id", "order_value", "status"])\n\nprint("Rows out:", len(clean_df))\nprint("Rows dropped:", len(df) - len(clean_df))\n\nassert len(clean_df) <= len(df)\nassert df.duplicated().sum() >= 1\nassert df.isnull().sum().sum() >= 1\n',
      description: 'Reference the committed repo file, then run a tiny scenario-matched proof with pandas.',
    },
    logic: {
      type: STEP_TYPES.LOGIC_FORM,
      prompts: [
        'Why does this approach work?',
        'What does Python do step by step?',
        'What rule or built-in behaviour makes this possible?',
      ],
      description: 'Explain the reasoning, not the steps.',
    },
    analogy: {
      type: STEP_TYPES.ANALOGY,
      hints: ['Think of an everyday object or activity that behaves the same way'],
      description: 'Find a real-world comparison that makes the concept click.',
    },
    story: {
      type: STEP_TYPES.STORY,
      roles: ['junior developer', 'teacher', 'shop assistant', 'student'],
      places: ['a startup', 'a classroom', 'a warehouse', 'a hospital'],
      description: 'Frame the problem as a real scenario with a person and a problem.',
    },
    visual: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'python',
      placeholder: '# Show the data or result visually — print a table, before/after, or draw a chart\n\n',
      description: 'Produce something you can see: a chart, a formatted table, or a state trace.',
    },
  },

  'data-analysis': {
    practical: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'python',
      placeholder: 'import pandas as pd\n\n# Write the exact code to produce the analysis\n# Tip: switch to DuckDB to query CSV data with SQL\n',
      description: 'Run the full analysis in code. Use Python or switch to DuckDB for SQL-style analysis.',
    },
    logic: {
      type: STEP_TYPES.LOGIC_FORM,
      prompts: [
        'Why do these pandas operations produce the right result?',
        'What is pandas doing internally?',
        'What rule or principle makes this work?',
      ],
      description: 'Explain why the operation works, not just how to write it.',
    },
    analogy: {
      type: STEP_TYPES.ANALOGY,
      hints: ['Think of Excel equivalents', 'Think of sorting physical objects into groups'],
      description: 'Compare to something familiar — Excel, filing cabinets, receipts.',
    },
    story: {
      type: STEP_TYPES.STORY,
      roles: ['data analyst', 'business analyst', 'manager', 'researcher'],
      places: ['a retail company', 'a hospital', 'a bank', 'an e-commerce site'],
      description: 'Frame the analysis as a real business question with a stakeholder.',
    },
    visual: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'python',
      placeholder: 'import pandas as pd\nimport matplotlib\nmatplotlib.use("Agg")\nimport matplotlib.pyplot as plt\n\n# Show your result as a chart or formatted table\n',
      description: 'Produce a chart or formatted table from code.',
    },
  },

  sql: {
    practical: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'duckdb',
      placeholder: '-- Write the exact SQL that answers the question\nSELECT\n',
      description: 'Write and run the query against lesson_data. Show the result.',
    },
    logic: {
      type: STEP_TYPES.LOGIC_FORM,
      prompts: [
        'Why does this SQL produce the right result?',
        'What does the database engine do step by step?',
        'What rule makes GROUP BY or JOIN work here?',
      ],
      description: 'Explain what the database engine is actually doing.',
    },
    analogy: {
      type: STEP_TYPES.ANALOGY,
      hints: ['Think of sorting physical files', 'Think of looking something up in a notebook or index'],
      description: 'Find a real-world comparison for the SQL operation.',
    },
    story: {
      type: STEP_TYPES.STORY,
      roles: ['database admin', 'analyst', 'developer', 'manager'],
      places: ['a bank', 'a hospital', 'an e-commerce platform', 'a school'],
      description: 'Frame the query as a real business need.',
    },
    visual: {
      type: STEP_TYPES.CODE_RUNNER,
      language: 'duckdb',
      placeholder: '-- Show the result as a formatted table\n-- DuckDB supports PIVOT, UNPIVOT, list_aggregate, and more\nSELECT\n  column1,\n  COUNT(*) AS total\nFROM lesson_data\nGROUP BY column1\nORDER BY total DESC;',
      description: 'Run a query that shows the shape or summary of the result set.',
    },
  },

  excel: {
    practical: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'List the exact steps you took in Excel',
        'What formula or feature did you use?',
        'What cells or ranges were involved?',
      ],
      description: 'Describe the exact Excel steps as if teaching someone else.',
    },
    logic: {
      type: STEP_TYPES.LOGIC_FORM,
      prompts: [
        'Why does this Excel formula give the right answer?',
        'What does Excel calculate step by step?',
        'What would happen if you used a different formula?',
      ],
      description: 'Explain what Excel is actually computing.',
    },
    analogy: {
      type: STEP_TYPES.ANALOGY,
      hints: ['Think of a real-world counting, sorting, or lookup task'],
      description: 'Compare the Excel operation to something familiar.',
    },
    story: {
      type: STEP_TYPES.STORY,
      roles: ['financial analyst', 'accountant', 'project manager', 'sales manager'],
      places: ['a business', 'a school', 'a hospital', 'a retail store'],
      description: 'Frame the spreadsheet task as a real business scenario.',
    },
    visual: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'Describe the spreadsheet layout — columns, rows, key cells',
        'What does the final result look like on the screen?',
      ],
      description: 'Describe or sketch the spreadsheet layout and result.',
    },
  },
};

const GENERIC_FALLBACK = FIVE_HOW_CONFIGS['data-analysis'];

const TASK_TYPE_ALIASES = {
  'python': 'python',
  'python-developer': 'python',
  'sql': 'sql',
  'sql-fundamentals': 'sql',
  'excel': 'excel',
  'data-analysis': 'data-analysis',
  'data-analyst': 'data-analysis',
  'azure-data-engineer': 'data-analysis',
  'data-engineer': 'data-analysis',
};

export function normalizeTaskType(key = '') {
  if (!key) return 'generic';
  const k = String(key).toLowerCase().replace(/_/g, '-');
  if (TASK_TYPE_ALIASES[k]) return TASK_TYPE_ALIASES[k];
  if (k.includes('python')) return 'python';
  if (k.includes('sql')) return 'sql';
  if (k.includes('excel')) return 'excel';
  if (k.includes('data')) return 'data-analysis';
  return 'generic';
}

export function getSolveConfig(taskType) {
  return SOLVE_CONFIGS[normalizeTaskType(taskType)] || SOLVE_CONFIGS.generic;
}

export function getFiveHowConfig(taskType) {
  return FIVE_HOW_CONFIGS[normalizeTaskType(taskType)] || GENERIC_FALLBACK;
}

export function isStepAnswered(response) {
  if (!response) return false;
  if (typeof response === 'string') return response.trim().length > 0;
  if (typeof response === 'object') {
    const { text, code, items = [], tools = [], answers = {} } = response;
    return (
      (typeof text === 'string' && text.trim().length > 0) ||
      (typeof code === 'string' && code.trim().length > 0) ||
      items.length > 0 ||
      tools.length > 0 ||
      Object.values(answers).some((v) => typeof v === 'string' && v.trim().length > 0)
    );
  }
  return false;
}
